// Versiyon: 2.0.0 - Çek/Senet Sayfası (API Entegrasyonlu)
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  Menu,
  MenuItem,
  ButtonGroup,
  CircularProgress,
  Alert,
  TablePagination,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Snackbar,
  Divider
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CheckIcon from '@mui/icons-material/Check';
import DescriptionIcon from '@mui/icons-material/Description';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import { REMINDER_SEARCH_QUERY } from '../../../utils/reminderDeepLink';
import firebaseDataService from '../../../services/FirebaseDataService';
import {
  CheckNoteDto,
  CheckNoteSummaryDto,
  CheckNoteType,
  CheckNoteDirection,
  CheckNoteStatus,
  CheckNoteTypeValue,
  CheckNoteDirectionValue,
  CheckNoteStatusValue,
  getStatusColor,
  formatCurrency,
  getCheckNotes
} from '../../../services/checkNoteService';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import {
  CHECK_NOTES_FILTERS_EVENT,
  consumePendingCheckNotesFilters,
  type CheckNotesFilterRequest,
} from '../../aichat/agent/uiBridge';

// Modal bileşenlerini import et
import AddCheckNoteModal from './AddCheckNoteModal';
import CheckNoteDetailModal from './CheckNoteDetailModal';
import ChangeStatusModal from './ChangeStatusModal';
import CheckNoteDeleteConfirmModal from './CheckNoteDeleteConfirmModal';
import BatchPromissoryNoteModal from './BatchPromissoryNoteModal';
import AccountingDataTable from '../shared/AccountingDataTable';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import { DATATABLE_STYLES } from '../shared/AccountingDataTable';
import ProjectBadge from '../shared/ProjectBadge';
import GuideButton from '../../common/guide/GuideButton';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS } from '../shared/StatsCard';

// Tema renkleri
const THEME_COLORS = {
  received: '#4CAF50',    // Green - Alınan
  given: '#F44336',       // Red - Verilen
  portfolio: '#2196F3',   // Blue - Portföy
  overdue: '#FF9800',     // Orange - Vadesi Geçen
};

const ChecksNotes: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation('common2');
  const localeCode = i18n.language?.toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR';

  const decodeMojibake = (value?: string | null): string => {
    if (!value) return '';
    let decoded = value;
    for (let i = 0; i < 2; i += 1) {
      if (!/[ÃÂÅÄ]/.test(decoded)) break;
      try {
        const next = decodeURIComponent(escape(decoded));
        if (!next || next === decoded) break;
        decoded = next;
      } catch {
        break;
      }
    }
    return decoded;
  };

  const getLocalizedTypeLabel = (type: CheckNoteTypeValue): string => {
    if (type === CheckNoteType.CHECK) return t('checksNotes.types.check');
    if (type === CheckNoteType.PROMISSORY_NOTE) return t('checksNotes.types.promissoryNote');
    return type;
  };

  const getLocalizedDirectionLabel = (direction: CheckNoteDirectionValue): string => {
    if (direction === CheckNoteDirection.RECEIVED) return t('checksNotes.directions.received');
    if (direction === CheckNoteDirection.GIVEN) return t('checksNotes.directions.given');
    return direction;
  };

  const getLocalizedStatusLabel = (status: CheckNoteStatusValue): string => {
    const keyMap: Record<CheckNoteStatusValue, string> = {
      [CheckNoteStatus.PORTFOLIO]: 'portfolio',
      [CheckNoteStatus.IN_COLLECTION]: 'inCollection',
      [CheckNoteStatus.COLLECTED]: 'collected',
      [CheckNoteStatus.PARTIAL_COLLECTED]: 'partialCollected',
      [CheckNoteStatus.ENDORSED]: 'endorsed',
      [CheckNoteStatus.COLLATERAL]: 'collateral',
      [CheckNoteStatus.COLLATERAL_RETURNED]: 'collateralReturned',
      [CheckNoteStatus.BOUNCED]: 'bounced',
      [CheckNoteStatus.RETURNED]: 'returned',
      [CheckNoteStatus.CANCELED]: 'canceled',
      [CheckNoteStatus.GIVEN_PENDING]: 'givenPending',
      [CheckNoteStatus.GIVEN_PAID]: 'givenPaid',
      [CheckNoteStatus.GIVEN_RETURNED]: 'givenReturned',
    };
    return t(`checksNotes.statuses.${keyMap[status]}`);
  };
  
  // === STATE YÖNETİMİ ===
  
  // Filtreleri göster/gizle
  const [showFilters, setShowFilters] = useState(false);
  
  // 3 nokta menüsü için state
  const [anchorElFeatures, setAnchorElFeatures] = useState<null | HTMLElement>(null);
  
  // Sıralama durumu için state
  const [sortColumn, setSortColumn] = useState<string>('dueDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Arama terimi için state (bildirimden ?q= ile gelebilir)
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get(REMINDER_SEARCH_QUERY)?.trim() ?? '');

  useEffect(() => {
    const q = searchParams.get(REMINDER_SEARCH_QUERY)?.trim();
    if (q) setSearchTerm(q);
  }, [searchParams]);
  
  // Filtreler
  const [typeFilter, setTypeFilter] = useState<CheckNoteTypeValue | 'all'>('all');
  const [directionFilter, setDirectionFilter] = useState<CheckNoteDirectionValue | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<CheckNoteStatusValue | 'all'>('all');
  const [dueDateFilter, setDueDateFilter] = useState<'all' | 'overdue' | 'thisWeek' | 'thisMonth'>('all');
  
  // Açılır menü için state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<CheckNoteDto | null>(null);

  // Silme onay modalı (ilişkili hareketlerle birlikte)
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  
  // Veri durumu için state
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [checksNotes, setChecksNotes] = useState<CheckNoteDto[]>([]);
  const [summary, setSummary] = useState<CheckNoteSummaryDto | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  
  // Sayfalama için state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  
  // Excel dışa aktarma için loading state'i
  const [loadingExport, setLoadingExport] = useState(false);
  
  // Modal state'leri
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addModalType, setAddModalType] = useState<CheckNoteTypeValue>(CheckNoteType.CHECK);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState<{open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning'}>({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // === HANDLER FONKSİYONLARI ===
  
  // 3 nokta menüsü handler'ları
  const handleOpenFeaturesMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElFeatures(event.currentTarget);
  };
  
  const handleCloseFeaturesMenu = () => {
    setAnchorElFeatures(null);
  };
  
  // Sayfa değişimi handler
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };
  
  // Sayfa başına satır sayısı değişimi handler
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Sıralama değişikliği için handler
  const handleSortChange = (column: string) => {
    if (column === sortColumn) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setPage(0);
  };
  
  // Menü açma handler'ı
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, item: CheckNoteDto) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };
  
  // Menü kapatma handler'ı
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  // Tarihi DD.MM.YYYY formatına dönüştürme
  const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    return date.toLocaleDateString(localeCode, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Para birimini formatla (TL işareti sonda)
  const formatCurrencyLocal = (amount: number, currency: string = 'TRY'): string => {
    const formatted = new Intl.NumberFormat(localeCode, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
    const symbol = currency === 'TRY' ? '₺' : (currency === 'USD' ? '$' : (currency === 'EUR' ? '€' : currency));
    return `${formatted} ${symbol}`;
  };

  // AccountingDataTable kolon tanımları
  const checkNoteColumns = useMemo<DataTableColumn<CheckNoteDto>[]>(() => [
    {
      id: 'rowNumber',
      label: '#',
      width: '50px',
      render: (_row, index) => (
        <Box component="span" sx={{ color: 'text.secondary' }}>{page * rowsPerPage + index + 1}</Box>
      ),
    },
    {
      id: 'documentNumber',
      label: t('checksNotes.table.documentNumber'),
      field: 'documentNumber',
      sortable: true,
    },
    {
      id: 'type',
      label: t('checksNotes.table.type'),
      field: 'type',
      sortable: true,
      render: (row) => <>{getLocalizedTypeLabel(row.type)}</>,
    },
    {
      id: 'direction',
      label: t('checksNotes.table.direction'),
      field: 'direction',
      sortable: true,
      render: (row) => (
        <Box component="span" sx={{ color: row.direction === CheckNoteDirection.RECEIVED ? 'success.main' : 'error.main', fontWeight: 500 }}>
          {getLocalizedDirectionLabel(row.direction)}
        </Box>
      ),
    },
    {
      id: 'accountName',
      label: t('checksNotes.table.accountName'),
      field: 'accountName',
      sortable: true,
      tooltip: true,
      // Proje bağı cari adının altında küçük etiket olarak gösterilir (PYS)
      render: (row) => (
        <Box>
          <Box component="span">{decodeMojibake(row.accountName) || '-'}</Box>
          <Box><ProjectBadge projectName={row.projectName} /></Box>
        </Box>
      ),
    },
    {
      id: 'bankName',
      label: t('checksNotes.table.bankName'),
      field: 'bankName',
      sortable: true,
      render: (row) => <>{decodeMojibake(row.bankName) || '-'}</>,
    },
    {
      id: 'dueDate',
      label: t('checksNotes.table.dueDate'),
      field: 'dueDate',
      sortable: true,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {formatDate(row.dueDate)}
          {row.isOverdue && (
            <Tooltip title={t('checksNotes.overdue')}>
              <WarningAmberIcon color="warning" sx={{ fontSize: 14 }} />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      id: 'amount',
      label: t('checksNotes.table.amount'),
      field: 'amount',
      align: 'right',
      sortable: true,
      render: (row) => (
        // Banka ve kasa görünümleriyle tutarlı olsun diye Typography kullanıldı:
        // variant="body2", fontWeight bold, success renk, sağa hizalı; yapı bozulmasın diye sadece sunum değiştirildi.
        <Typography
          variant="body2"
          fontWeight="bold"
          color="success.main"
          sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}
        >
          {row.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
          {row.currency === 'USD' ? '$' : row.currency === 'EUR' ? '€' : '₺'}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: t('checksNotes.table.status'),
      field: 'status',
      sortable: true,
      render: (row) => (
        <Chip
          label={getLocalizedStatusLabel(row.status)}
          size="small"
          color={getStatusColor(row.status) as 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' | 'default'}
          // Siparişler sayfasıyla aynı stil: dolu chip, tüm durumlar yatayda eşit genişlik, ortaya hizalı
          sx={{ minWidth: '140px', justifyContent: 'center' }}
        />
      ),
    },
    {
      id: 'actions',
      label: t('checksNotes.table.actions'),
      align: 'center' as const,
      render: (row) => (
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, row); }}
          sx={{ padding: '2px' }}
        >
          <MoreVertIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      ),
    },
  ], [page, rowsPerPage, t]);

  // Satır koşullu arka plan renkleri
  const getCheckNoteRowSx = (row: CheckNoteDto) => ({
    bgcolor: row.isOverdue
      ? 'rgba(255, 152, 0, 0.15)'
      : row.daysUntilDue === 0
        ? 'rgba(76, 175, 80, 0.15)'
        : row.daysUntilDue > 0
          ? 'rgba(158, 158, 158, 0.15)'
          : 'inherit',
  });

  // === VERİ YÜKLEME ===
  
  // Çek/Senetleri yükle
  const loadChecksNotes = useCallback(async (compId: string) => {
    console.log("📤 [API] Çek/Senetler yükleniyor...", compId);
    setLoading(true);
    
    try {
      // Filtre parametreleri oluştur
      const filters = {
        type: typeFilter !== 'all' ? typeFilter : undefined,
        direction: directionFilter !== 'all' ? directionFilter : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        searchTerm: searchTerm || undefined,
        pageNumber: page + 1,
        pageSize: rowsPerPage,
        sortBy: sortColumn,
        sortDirection: sortDirection,
        // Vade tarihi filtreleri
        ...(dueDateFilter === 'overdue' && { isOverdue: true }),
        ...(dueDateFilter === 'thisWeek' && { 
          dueDateFrom: new Date().toISOString().split('T')[0],
          dueDateTo: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        }),
        ...(dueDateFilter === 'thisMonth' && { 
          dueDateFrom: new Date().toISOString().split('T')[0],
          dueDateTo: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0]
        }),
      };
      
      const response = await getCheckNotes({
        ...filters
      });
      
      setChecksNotes(response.items);
      setTotalCount(response.totalCount);
    } catch (error) {
      console.error('❌ [API] Çek/Senetler yüklenirken hata:', error);
      setChecksNotes([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, directionFilter, statusFilter, searchTerm, page, rowsPerPage, sortColumn, sortDirection, dueDateFilter]);

  // Özet bilgileri yükle - getCheckNotes response zaten summary içeriyor
  const loadSummary = useCallback(async (compId: string) => {
    try {
      // getCheckNotes kullanarak hem listeyi hem özeti alıyoruz
      const response = await getCheckNotes({
        pageSize: 1
      });
      setSummary(response.summary);
    } catch (error) {
      console.error('❌ [API] Özet bilgiler yüklenirken hata:', error);
    }
  }, []);

  // Şirket ID'sini ve verileri yükleme
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const uid = ownAuthSession.getCurrentUid();
        if (!uid) {
          setError(t('checksNotes.errors.noSession'));
          setLoading(false);
          return;
        }
        
        const userData = await firebaseDataService.getUserData(uid);
        
        if (userData && userData.companyId) {
          const userCompanyId = userData.companyId;
          setCompanyId(userCompanyId);
          
          // Çek/Senetleri ve özet bilgileri yükle
          await Promise.all([
            loadChecksNotes(userCompanyId),
            loadSummary(userCompanyId)
          ]);
        } else {
          setError(t('checksNotes.errors.noUserData'));
          setLoading(false);
        }
      } catch (err) {
        console.error('Veri yükleme hatası:', err);
        setError(t('checksNotes.errors.loadError'));
        setLoading(false);
      }
    };
    
    loadInitialData();
  }, [t, loadChecksNotes, loadSummary]);

  // Filtre değişikliklerinde verileri yeniden yükle
  useEffect(() => {
    if (companyId) {
      loadChecksNotes(companyId);
    }
  }, [companyId, typeFilter, directionFilter, statusFilter, dueDateFilter, searchTerm, page, rowsPerPage, sortDirection, loadChecksNotes]);

  // Arama/filtre yapıldığında sayfayı sıfırla
  useEffect(() => {
    setPage(0);
  }, [searchTerm, typeFilter, directionFilter, statusFilter, dueDateFilter, sortDirection]);

  // AI ajanı: sayfa filtrelerini doldur (stok filtresi köprüsüyle aynı desen)
  useEffect(() => {
    const applyAgentFilters = (req: CheckNotesFilterRequest) => {
      if (!req) return;
      if (req.clearAll) {
        setSearchTerm('');
        setTypeFilter('all');
        setDirectionFilter('all');
        setStatusFilter('all');
        setDueDateFilter('all');
      }
      if (req.searchText !== undefined) setSearchTerm(req.searchText);
      if (req.type !== undefined) {
        const t = String(req.type).toUpperCase();
        if (t === 'ALL' || t === '') setTypeFilter('all');
        else if (t === 'CHECK' || t === 'PROMISSORY_NOTE') setTypeFilter(t as CheckNoteTypeValue);
      }
      if (req.direction !== undefined) {
        const d = String(req.direction).toUpperCase();
        if (d === 'ALL' || d === '') setDirectionFilter('all');
        else if (d === 'RECEIVED' || d === 'GIVEN') setDirectionFilter(d as CheckNoteDirectionValue);
      }
      if (req.status !== undefined) {
        const s = String(req.status).toUpperCase();
        if (s === 'ALL' || s === '') setStatusFilter('all');
        else setStatusFilter(s as CheckNoteStatusValue);
      }
      if (req.dueDateFilter !== undefined) {
        const df = String(req.dueDateFilter);
        if (df === 'all' || df === 'overdue' || df === 'thisWeek' || df === 'thisMonth') {
          setDueDateFilter(df);
        }
      }
      setPage(0);
      setShowFilters(true);
    };

    const pending = consumePendingCheckNotesFilters();
    if (pending) applyAgentFilters(pending);

    const handler = (e: Event) => {
      consumePendingCheckNotesFilters();
      applyAgentFilters((e as CustomEvent<CheckNotesFilterRequest>).detail);
    };
    window.addEventListener(CHECK_NOTES_FILTERS_EVENT, handler);
    return () => window.removeEventListener(CHECK_NOTES_FILTERS_EVENT, handler);
  }, []);
  
  // === İŞLEM FONKSİYONLARI ===
  
  // Yeni Çek/Senet ekleme modalını aç
  const handleOpenAddModal = (type: CheckNoteTypeValue) => {
    setAddModalType(type);
    setEditMode(false);
    setAddModalOpen(true);
  };

  // Modal'dan gelen kaydetme
  const handleSaveCheckNote = async () => {
    setAddModalOpen(false);
    if (companyId) {
      await loadChecksNotes(companyId);
      await loadSummary(companyId);
    }
    setSnackbar({
      open: true,
      message: editMode ? t('checksNotes.messages.updateSuccess') : t('checksNotes.messages.createSuccess'),
      severity: 'success'
    });
  };

  // Detay modalını aç
  const handleViewItem = (item: CheckNoteDto) => {
    setSelectedItem(item);
    setDetailModalOpen(true);
  };

  // Düzenleme
  const handleEditItem = (item: CheckNoteDto) => {
    setSelectedItem(item);
    setAddModalType(item.type);
    setEditMode(true);
    setAddModalOpen(true);
  };
  
  // Durum değiştirme modalını aç
  const handleOpenStatusModal = (item: CheckNoteDto) => {
    setSelectedItem(item);
    setStatusModalOpen(true);
  };

  // Durum değişikliği sonrası
  const handleStatusChanged = async () => {
    setStatusModalOpen(false);
    if (companyId) {
      await loadChecksNotes(companyId);
      await loadSummary(companyId);
    }
    setSnackbar({
      open: true,
      message: t('checksNotes.messages.statusChangeSuccess'),
      severity: 'success'
    });
  };

  // Yenile
  const handleRefresh = () => {
    if (companyId) {
      loadChecksNotes(companyId);
      loadSummary(companyId);
    }
  };

  // Excel'e aktarma fonksiyonu
  const exportToExcel = async () => {
    if (!companyId) return;
    
    try {
      setLoadingExport(true);
      
      // Tüm verileri al (sayfalama olmadan)
      const response = await getCheckNotes({
        type: typeFilter !== 'all' ? typeFilter as CheckNoteTypeValue : undefined,
        direction: directionFilter !== 'all' ? directionFilter as CheckNoteDirectionValue : undefined,
        status: statusFilter !== 'all' ? statusFilter as CheckNoteStatusValue : undefined,
        pageSize: 10000
      });
      
      const dataToExport = response.items;
      
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t('checksNotes.title'));
      
      // Başlık satırı
      worksheet.mergeCells('A1:J1');
      const titleCell = worksheet.getCell('A1');
      titleCell.value = t('checksNotes.title');
      titleCell.style = {
        font: { bold: true, size: 14, color: { argb: 'FF000000' } },
        alignment: { horizontal: 'center' }
      };
      
      worksheet.addRow([]);
      
      // Tablo başlıkları
      const headerRow = worksheet.addRow([
        t('checksNotes.table.documentNumber'),
        t('checksNotes.table.type'),
        t('checksNotes.table.direction'),
        t('checksNotes.table.accountName'),
        t('checksNotes.table.bankName'),
        t('checksNotes.table.issueDate'),
        t('checksNotes.table.dueDate'),
        t('checksNotes.table.amount'),
        t('checksNotes.table.currency'),
        t('checksNotes.table.status')
      ]);
      
      headerRow.eachCell((cell) => {
        cell.style = {
          font: { bold: true, color: { argb: 'FFFFFFFF' } },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F81BD' } },
          border: {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          }
        };
      });
      
      // Veri satırları
      dataToExport.forEach((item: CheckNoteDto) => {
        worksheet.addRow([
          item.documentNumber,
          getLocalizedTypeLabel(item.type),
          getLocalizedDirectionLabel(item.direction),
          item.accountName || '-',
          item.bankName || '-',
          formatDate(item.issueDate),
          formatDate(item.dueDate),
          item.amount,
          item.currency,
          getLocalizedStatusLabel(item.status)
        ]);
      });
      
      // Sütun genişlikleri
      worksheet.columns = [
        { width: 15 },
        { width: 12 },
        { width: 12 },
        { width: 25 },
        { width: 20 },
        { width: 12 },
        { width: 12 },
        { width: 15 },
        { width: 8 },
        { width: 15 }
      ];
      
      worksheet.getColumn(8).numFmt = '#,##0.00';
      
      // Zebrastripe
      for (let i = 4; i <= worksheet.rowCount; i++) {
        const fillColor = i % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF';
        worksheet.getRow(i).eachCell(cell => {
          cell.style = {
            ...cell.style,
            fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: fillColor } },
            border: {
              top: { style: 'thin' as const },
              left: { style: 'thin' as const },
              bottom: { style: 'thin' as const },
              right: { style: 'thin' as const }
            }
          };
        });
      }
      
      // Dosya adı
      const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const fileName = `Cek_Senet_Listesi_${currentDate}.xlsx`;
      
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), fileName);
      
      setSnackbar({
        open: true,
        message: t('checksNotes.messages.exportSuccess'),
        severity: 'success'
      });
    } catch (error) {
      console.error('Excel dışa aktarma hatası:', error);
      setSnackbar({
        open: true,
        message: t('checksNotes.errors.exportError'),
        severity: 'error'
      });
    } finally {
      setLoadingExport(false);
    }
  };

  return (
    <>
      {/* Üst Başlık */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
          <ReceiptLongIcon sx={{ fontSize: { xs: 28, md: 32 }, mr: 1.5, color: 'primary.main' }} />
          <Typography variant={isMobile ? "h6" : "h5"} component="h1" fontWeight="bold">
            {t('checksNotes.title')}
          </Typography>
          <GuideButton guideKey="finance.checksNotes" sx={{ ml: 0.5 }} />
        </Box>
      </Box>

      {/* Özet Kartları */}
      {summary && (
        <StatsCardsContainer>
          {/* Toplam Alınan */}
          <Grid item xs={12} sm={6} md={3}>
            <StatsCardItem
              icon={<TrendingUpIcon />}
              color={THEME_COLORS.received}
              title={formatCurrency(summary.receivedCheckTotalTRY + summary.receivedNoteTotalTRY)}
              subtitle={`${t('checksNotes.summary.receivedTotal')} (${summary.receivedCheckCount + summary.receivedNoteCount} ${t('checksNotes.summary.items')})`}
            />
          </Grid>
          
          {/* Toplam Verilen */}
          <Grid item xs={12} sm={6} md={3}>
            <StatsCardItem
              icon={<TrendingDownIcon />}
              color={THEME_COLORS.given}
              title={formatCurrency(summary.givenCheckTotalTRY + summary.givenNoteTotalTRY)}
              subtitle={`${t('checksNotes.summary.givenTotal')} (${summary.givenCheckCount + summary.givenNoteCount} ${t('checksNotes.summary.items')})`}
            />
          </Grid>
          
          {/* Portföyde */}
          <Grid item xs={12} sm={6} md={3}>
            <StatsCardItem
              icon={<AccountBalanceIcon />}
              color={THEME_COLORS.portfolio}
              title={formatCurrency(summary.portfolioTotal)}
              subtitle={`${t('checksNotes.summary.portfolioTotal')} (${summary.portfolioCount} ${t('checksNotes.summary.items')})`}
            />
          </Grid>
          
          {/* Vadesi Geçen */}
          <Grid item xs={12} sm={6} md={3}>
            <StatsCardItem
              icon={<WarningAmberIcon />}
              color={summary.overdueCount > 0 ? THEME_COLORS.overdue : STATS_COLORS.grey}
              title={formatCurrency(summary.overdueTotal)}
              subtitle={`${t('checksNotes.summary.overdueTotal')} (${summary.overdueCount} ${t('checksNotes.summary.items')})`}
            />
          </Grid>
        </StatsCardsContainer>
      )}

      {/* Arama/Filtre ve Butonlar - Aynı Satır */}
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Arama Alanı */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('checksNotes.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Filtreleri Göster İkonu */}
          <Grid item xs={12} md={0.5} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
            <Tooltip title={showFilters ? t('checksNotes.hideFilters') : t('checksNotes.showFilters')}>
              <IconButton
                color={showFilters ? "primary" : "default"}
                onClick={() => setShowFilters(!showFilters)}
                size="small"
              >
                <FilterListIcon />
              </IconButton>
            </Tooltip>
          </Grid>

          {/* Desktop Butonlar - Aynı Satır */}
          <Grid item xs={12} md={8.5} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end', gap: 1 }}>
            <ButtonGroup>
              <Button 
                variant="contained" 
                color="primary"
                startIcon={<CheckIcon />}
                onClick={() => handleOpenAddModal(CheckNoteType.CHECK)}
              >
                {t('checksNotes.newCheck')}
              </Button>
              <Button 
                variant="contained" 
                color="secondary"
                startIcon={<DescriptionIcon />}
                onClick={() => handleOpenAddModal(CheckNoteType.PROMISSORY_NOTE)}
              >
                {t('checksNotes.newPromissoryNote')}
              </Button>
              <Tooltip title={t('checksNotes.batch.openModal')}>
                <Button 
                  variant="outlined" 
                  color="secondary"
                  startIcon={<PlaylistAddIcon />}
                  onClick={() => setBatchModalOpen(true)}
                >
                  {t('checksNotes.batch.shortButton')}
                </Button>
              </Tooltip>
              <Tooltip title={t('checksNotes.refresh')}>
                <Button
                  variant="outlined"
                  onClick={handleRefresh}
                  sx={{ minWidth: 'auto', px: 1 }}
                >
                  <RefreshIcon />
                </Button>
              </Tooltip>
              <Button
                variant="outlined"
                onClick={handleOpenFeaturesMenu}
                sx={{ minWidth: 'auto', px: 1 }}
              >
                <MoreVertIcon />
              </Button>
            </ButtonGroup>
          </Grid>

          {/* Mobil Butonlar */}
          <Grid item xs={12} sx={{ display: { xs: 'grid', md: 'none' } }}>
            <Grid container spacing={1}>
              <Grid item xs={12} sx={{ mb: 1 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  color={showFilters ? "primary" : "inherit"}
                  startIcon={<FilterListIcon />}
                  onClick={() => setShowFilters(!showFilters)}
                  size="small"
                >
                  {showFilters ? t('checksNotes.hideFilters') : t('checksNotes.showFilters')}
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button 
                  fullWidth
                  size="small"
                  variant="contained" 
                  color="primary"
                  startIcon={<CheckIcon />}
                  onClick={() => handleOpenAddModal(CheckNoteType.CHECK)}
                >
                  {t('checksNotes.newCheck')}
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button 
                  fullWidth
                  size="small"
                  variant="contained" 
                  color="secondary"
                  startIcon={<DescriptionIcon />}
                  onClick={() => handleOpenAddModal(CheckNoteType.PROMISSORY_NOTE)}
                >
                  {t('checksNotes.newPromissoryNote')}
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button 
                  fullWidth
                  size="small"
                  variant="outlined" 
                  color="secondary"
                  startIcon={<PlaylistAddIcon />}
                  onClick={() => setBatchModalOpen(true)}
                >
                  {t('checksNotes.batch.shortButton')}
                </Button>
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button 
                  size="small"
                  variant="outlined"
                  onClick={handleOpenFeaturesMenu}
                  sx={{ minWidth: 'auto' }}
                >
                  <MoreVertIcon fontSize="small" />
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* Filtreler - showFilters true ise göster */}
      {showFilters && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Tür Filtresi */}
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('checksNotes.filters.type')}</InputLabel>
                <Select
                  value={typeFilter}
                  label={t('checksNotes.filters.type')}
                  onChange={(e: SelectChangeEvent) => setTypeFilter(e.target.value as CheckNoteTypeValue | 'all')}
                >
                  <MenuItem value="all">{t('checksNotes.filters.all')}</MenuItem>
                  <MenuItem value={CheckNoteType.CHECK}>{t('checksNotes.types.check')}</MenuItem>
                  <MenuItem value={CheckNoteType.PROMISSORY_NOTE}>{t('checksNotes.types.promissoryNote')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Yön Filtresi */}
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('checksNotes.filters.direction')}</InputLabel>
                <Select
                  value={directionFilter}
                  label={t('checksNotes.filters.direction')}
                  onChange={(e: SelectChangeEvent) => setDirectionFilter(e.target.value as CheckNoteDirectionValue | 'all')}
                >
                  <MenuItem value="all">{t('checksNotes.filters.all')}</MenuItem>
                  <MenuItem value={CheckNoteDirection.RECEIVED}>{t('checksNotes.directions.received')}</MenuItem>
                  <MenuItem value={CheckNoteDirection.GIVEN}>{t('checksNotes.directions.given')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Durum Filtresi */}
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('checksNotes.filters.status')}</InputLabel>
                <Select
                  value={statusFilter}
                  label={t('checksNotes.filters.status')}
                  onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value as CheckNoteStatusValue | 'all')}
                >
                  <MenuItem value="all">{t('checksNotes.filters.all')}</MenuItem>
                  {/* Alınan çekler için durumlar */}
                  <MenuItem value={CheckNoteStatus.PORTFOLIO}>{t('checksNotes.statuses.portfolio')}</MenuItem>
                  <MenuItem value={CheckNoteStatus.IN_COLLECTION}>{t('checksNotes.statuses.inCollection')}</MenuItem>
                  <MenuItem value={CheckNoteStatus.COLLECTED}>{t('checksNotes.statuses.collected')}</MenuItem>
                  <MenuItem value={CheckNoteStatus.PARTIAL_COLLECTED}>{t('checksNotes.statuses.partialCollected')}</MenuItem>
                  <MenuItem value={CheckNoteStatus.ENDORSED}>{t('checksNotes.statuses.endorsed')}</MenuItem>
                  <MenuItem value={CheckNoteStatus.COLLATERAL}>{t('checksNotes.statuses.collateral')}</MenuItem>
                  <MenuItem value={CheckNoteStatus.BOUNCED}>{t('checksNotes.statuses.bounced')}</MenuItem>
                  <MenuItem value={CheckNoteStatus.RETURNED}>{t('checksNotes.statuses.returned')}</MenuItem>
                  <MenuItem value={CheckNoteStatus.CANCELED}>{t('checksNotes.statuses.canceled')}</MenuItem>
                  {/* Verilen çekler için durumlar */}
                  <MenuItem value={CheckNoteStatus.GIVEN_PENDING}>{t('checksNotes.statuses.givenPending')}</MenuItem>
                  <MenuItem value={CheckNoteStatus.GIVEN_PAID}>{t('checksNotes.statuses.givenPaid')}</MenuItem>
                  <MenuItem value={CheckNoteStatus.GIVEN_RETURNED}>{t('checksNotes.statuses.givenReturned')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Vade Tarihi Filtresi */}
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('checksNotes.filters.dueDate')}</InputLabel>
                <Select
                  value={dueDateFilter}
                  label={t('checksNotes.filters.dueDate')}
                  onChange={(e: SelectChangeEvent) => setDueDateFilter(e.target.value as any)}
                >
                  <MenuItem value="all">{t('checksNotes.filters.all')}</MenuItem>
                  <MenuItem value="overdue">{t('checksNotes.filters.overdue')}</MenuItem>
                  <MenuItem value="thisWeek">{t('checksNotes.filters.thisWeek')}</MenuItem>
                  <MenuItem value="thisMonth">{t('checksNotes.filters.thisMonth')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Sıralama */}
            <Grid item xs={6} md={2}>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={sortDirection === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                onClick={() => handleSortChange('dueDate')}
              >
                {sortDirection === 'asc' ? t('checksNotes.sort.oldToNew') : t('checksNotes.sort.newToOld')}
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {/* Ana İçerik */}
      <Box sx={{ mb: 3 }}>
        {/* Tablo - loading overlay ile */}
        <Box sx={{ position: 'relative' }}>
          {/* Loading Overlay */}
          {loading && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 10,
              borderRadius: 2,
            }}>
              <CircularProgress />
            </Box>
          )}
          
          {/* Desktop: AccountingDataTable */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <AccountingDataTable<CheckNoteDto>
              columns={checkNoteColumns}
              rows={checksNotes}
              getRowId={(row) => row.id.toString()}
              loading={loading}
              pagination={false}
              onRowClick={handleViewItem}
              getRowSx={getCheckNoteRowSx}
              emptyMessage={t('checksNotes.noData')}
              emptyIcon={<ReceiptLongIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
              ariaLabel="checks notes table"
              onSort={handleSortChange}
              sortField={sortColumn}
              sortDirection={sortDirection}
              columnResizeStorageKey="checks-notes"
            />
            
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage={t('checksNotes.pagination.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) => t('checksNotes.pagination.displayedRows', { from, to, count })}
              sx={DATATABLE_STYLES.pagination}
            />
          </Box>

          {/* Mobile: Card View */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 2 }}>
            {checksNotes.length === 0 ? (
              <Box sx={{ 
                p: 4, 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                justifyContent: 'center',
                height: '200px',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%)',
                borderRadius: 2
              }}>
                <ReceiptLongIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  {t('checksNotes.noData')}
                </Typography>
              </Box>
            ) : (
              checksNotes.map((item) => (
                <Card 
                  key={item.id}
                  onClick={() => handleViewItem(item)}
                  sx={{ 
                    border: '1px solid',
                    borderColor: item.type === CheckNoteType.CHECK ? 'primary.light' : 'secondary.light',
                    bgcolor: item.isOverdue ? 'rgba(255, 152, 0, 0.08)' : (item.type === CheckNoteType.CHECK ? 'rgba(25, 118, 210, 0.04)' : 'rgba(156, 39, 176, 0.04)'),
                    cursor: 'pointer'
                  }}
                >
                  <CardContent>
                    {/* Başlık - Belge No */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {item.type === CheckNoteType.CHECK ? (
                          <CheckIcon color="primary" sx={{ fontSize: 20 }} />
                        ) : (
                          <DescriptionIcon color="secondary" sx={{ fontSize: 20 }} />
                        )}
                        <Typography variant="subtitle1" fontWeight="bold">
                          {item.documentNumber}
                        </Typography>
                        {item.isOverdue && (
                          <WarningAmberIcon color="warning" sx={{ fontSize: 18 }} />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Chip 
                          label={getLocalizedTypeLabel(item.type)} 
                          size="small"
                          color={item.type === CheckNoteType.CHECK ? 'primary' : 'secondary'}
                          variant="outlined"
                        />
                        <Chip 
                          label={getLocalizedDirectionLabel(item.direction)} 
                          size="small"
                          color={item.direction === CheckNoteDirection.RECEIVED ? 'success' : 'error'}
                          variant="filled"
                        />
                      </Box>
                    </Box>
                    
                    {/* Cari Hesap */}
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('checksNotes.table.accountName')}:
                      </Typography>
                      <Typography variant="body2">
                        {decodeMojibake(item.accountName) || '-'}
                      </Typography>
                    </Box>
                    
                    {/* Vade Tarihi */}
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('checksNotes.table.dueDate')}:
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(item.dueDate)}
                      </Typography>
                    </Box>
                    
                    {/* Durum */}
                    <Box sx={{ mb: 1 }}>
                      <Chip 
                        label={getLocalizedStatusLabel(item.status)} 
                        size="small"
                        color={getStatusColor(item.status) as 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' | 'default'}
                        variant="outlined"
                      />
                    </Box>
                    
                    {/* Tutar */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 2, 
                      pt: 1.5, 
                      borderTop: '1px solid', 
                      borderColor: 'divider' 
                    }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('checksNotes.table.amount')}:
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color={item.type === CheckNoteType.CHECK ? 'primary.main' : 'secondary.main'}>
                        {formatCurrencyLocal(item.amount, item.currency)}
                      </Typography>
                    </Box>
                    
                    {/* Actions */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                      <IconButton 
                        size="small" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewItem(item);
                        }}
                        sx={{ bgcolor: 'action.hover' }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditItem(item);
                        }}
                        sx={{ bgcolor: 'action.hover' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small"
                        onClick={(e) => handleMenuOpen(e, item)}
                        sx={{ bgcolor: 'action.hover' }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ))
            )}
            
            {/* Mobile Pagination */}
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage=""
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
              sx={{
                '.MuiTablePagination-toolbar': {
                  paddingLeft: 1,
                  paddingRight: 1
                },
                '.MuiTablePagination-selectLabel': {
                  display: 'none'
                },
                '.MuiTablePagination-displayedRows': {
                  fontSize: '0.875rem'
                }
              }}
            />
          </Box>
        </Box>
      </Box>
      
      {/* 3 Nokta (Özellikler) Menüsü */}
      <Menu
        anchorEl={anchorElFeatures}
        open={Boolean(anchorElFeatures)}
        onClose={handleCloseFeaturesMenu}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem 
          onClick={() => {
            exportToExcel();
            handleCloseFeaturesMenu();
          }}
          disabled={loadingExport || loading || checksNotes.length === 0}
        >
          {loadingExport ? <CircularProgress size={16} sx={{ mr: 1 }} /> : <FileDownloadIcon fontSize="small" sx={{ mr: 1 }} />}
          {t('checksNotes.exportExcel')}
        </MenuItem>
      </Menu>
      
      {/* İşlemler Menüsü */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl) && Boolean(selectedItem)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => {
          if (selectedItem) {
            const item = selectedItem;
            handleMenuClose();
            setTimeout(() => handleViewItem(item), 0);
          }
        }}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
          {t('checksNotes.actions.view')}
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedItem) {
            const item = selectedItem;
            handleMenuClose();
            setTimeout(() => handleEditItem(item), 0);
          }
        }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          {t('checksNotes.actions.edit')}
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
          if (selectedItem) {
            const item = selectedItem;
            handleMenuClose();
            setTimeout(() => handleOpenStatusModal(item), 0);
          }
        }}>
          <SwapHorizIcon fontSize="small" sx={{ mr: 1 }} />
          {t('checksNotes.actions.changeStatus')}
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
          if (selectedItem) {
            const id = selectedItem.id;
            handleMenuClose();
            setTimeout(() => {
              setDeleteTargetId(id);
              setDeleteModalOpen(true);
            }, 0);
          }
        }} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          {t('checksNotes.actions.delete')}
        </MenuItem>
      </Menu>
      
      {/* Çek/Senet Ekleme/Düzenleme Modalı */}
      <AddCheckNoteModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={handleSaveCheckNote}
        type={addModalType}
        companyId={companyId || ''}
        editData={editMode ? selectedItem : undefined}
      />
      
      {/* Detay Modalı */}
      <CheckNoteDetailModal
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        checkNote={selectedItem}
        onEdit={() => selectedItem && handleEditItem(selectedItem)}
        onStatusChange={() => selectedItem && handleOpenStatusModal(selectedItem)}
      />
      
      {/* Durum Değiştirme Modalı */}
      <ChangeStatusModal
        open={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        onSuccess={handleStatusChanged}
        checkNote={selectedItem}
        companyId={companyId || ''}
      />

      {/* Silme Onay Modalı (ilişkili hareketlerle) */}
      <CheckNoteDeleteConfirmModal
        open={deleteModalOpen}
        checkNoteId={deleteTargetId}
        onClose={() => { setDeleteModalOpen(false); setDeleteTargetId(null); }}
        onConfirmDelete={async () => {
          if (companyId) {
            await loadChecksNotes(companyId);
            await loadSummary(companyId);
          }
          setSnackbar({
            open: true,
            message: t('checksNotes.messages.deleteSuccess'),
            severity: 'success',
          });
        }}
      />
      
      {/* Seri Senet Oluşturma Modalı */}
      <BatchPromissoryNoteModal
        open={batchModalOpen}
        onClose={() => setBatchModalOpen(false)}
        onSave={() => {
          handleRefresh();
          setSnackbar({
            open: true,
            message: t('checksNotes.messages.batchCreateSuccess'),
            severity: 'success'
          });
        }}
        companyId={companyId || ''}
      />
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ChecksNotes;
